import { Button, DialogActions, DialogTitle, ListItemIcon, MenuItem, Typography, type DialogActionsProps } from '@mui/material';
import type { TriggerReportJobRequestPayload } from '@weft/api-definition';
import { VyIcon } from '@weft/icons';
import type { ReportJobTypes } from '@weft/types';
import { REPORT_JOB_TYPES } from '@weft/types';
import { isNotNullOrUndefined, isNullOrUndefined, raiseUnreachable } from '@weft/utils';
import { DefaultThemeDialog } from 'apps/app/src/components/design/DefaultThemeDialog';
import { useTriggerReportJob } from 'apps/app/src/hooks/requests/use-trigger-report-job';
import { useDialog } from 'apps/app/src/hooks/use-dialog';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React from 'react';

export type TriggerReportJobFormProps = {
  onSubmit: (payload: TriggerReportJobRequestPayload) => void;
  dialogActions: React.ReactElement<DialogActionsProps>;
};

type TriggerReportJobDialogProps = {
  formContent?: (props: TriggerReportJobFormProps) => React.ReactElement<TriggerReportJobFormProps>;
  reportJobType?: ReportJobTypes;
  disabled?: boolean;
};

export const TriggerReportJobForm: React.FC<TriggerReportJobDialogProps> = ({ reportJobType, formContent, disabled = false }) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const triggerReportJobMutation = useTriggerReportJob();

  const handleFormSubmit = async (payload: TriggerReportJobRequestPayload) => {
    await runMutation(
      triggerReportJobMutation,
      payload,
      {
        pending: 'Requesting...',
        success: 'Report requested',
        error: 'Failed to request report',
      },
      {
        onSuccess: handleClose,
      },
    );
  };

  return (
    <>
      <MenuItem
        sx={{ height: '50px !important' }}
        disabled={isNullOrUndefined(reportJobType) || disabled}
        onClick={handleOpen}
      >
        <ListItemIcon>
          <VyIcon icon="Notebook"/>
        </ListItemIcon>
        <Typography
          fontSize={16}
          fontWeight={350}
        >
          Request a Report
        </Typography>
      </MenuItem>

      <DefaultThemeDialog
        open={isDialogOpen}
        onClose={handleClose}
        fullWidth
        PaperProps={{
          className: 'max-w-[550px] overflow-visible px-8 py-4',
        }}
      >
        {isNotNullOrUndefined(reportJobType) && <DialogTitle>{`Request a ${getReportTypeLabel(reportJobType)} report`}</DialogTitle>}

        {formContent?.({
          onSubmit: handleFormSubmit, dialogActions: ( 
            <>
              <DialogActions>
                <Button
                  variant="text"
                  color="secondary"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <button
                  type="submit"
                  className="inline-flex justify-center rounded-md border
                                    border-transparent bg-blue-100 px-4 py-2 text-base font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  Submit
                </button>
              </DialogActions>
            </>
          ),
        })}
      </DefaultThemeDialog>
    </>
  );
};

function getReportTypeLabel(reportType: ReportJobTypes): string {
  switch (reportType) {
    case REPORT_JOB_TYPES.CommitmentUsage:
      return 'commitment usage';
    case REPORT_JOB_TYPES.UnchargedUsage:
      return 'uncharged usage';
    case REPORT_JOB_TYPES.ProductsUsage:
      return 'product usage';
    case REPORT_JOB_TYPES.CustomerRevenue:
      return 'customer revenue';
    case REPORT_JOB_TYPES.Contracts:
      return 'contracts';
    case REPORT_JOB_TYPES.AnonymousCustomers:
      return 'anonymous customers';
    case REPORT_JOB_TYPES.MonthlyClosure:
      return 'monthly closure';
    default:
      return raiseUnreachable(reportType);
  }
}